import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, DoorOpen, History, UserPlus, Users } from 'lucide-react';

import ContratadoModal from './ContratadoModal';
import ContratadosTable from './ContratadosTable';
import api, { getApiErrorMessage } from '../../services/api';
import {
  FeedbackBanner,
  PageHeader,
  PageLayout,
  SearchField,
  SummaryCard,
  SummaryGrid,
} from '../../components/ui/PageLayout';

const PAGE_SIZE = 25;

const ContratadosPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contratados, setContratados] = useState([]);
  const [moradores, setMoradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoradores, setLoadingMoradores] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [contratadoParaEditar, setContratadoParaEditar] = useState(null);
  const [statusFilter, setStatusFilter] = useState('todos');
  const [feedback, setFeedback] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    total_presentes: 0,
    total_hoje: 0,
    total_filtrado: 0,
    total_pages: 1,
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setPage(1);
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  const fetchContratados = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/contratados/paginado', {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearchTerm || undefined,
          status: statusFilter,
        },
      });
      const data = response.data || {};
      const totalPages = data.total_pages || 1;

      setContratados(data.items || []);
      setPagination({
        total: data.total || 0,
        total_presentes: data.total_presentes || 0,
        total_hoje: data.total_hoje || 0,
        total_filtrado: data.total_filtrado || 0,
        total_pages: totalPages,
      });

      if (page > totalPages) {
        setPage(totalPages);
      }
    } catch (error) {
      console.error('Erro ao buscar contratados:', error);
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Erro ao carregar prestadores.') });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, page, statusFilter]);

  const fetchMoradores = useCallback(async () => {
    if (moradores.length || loadingMoradores) return;

    setLoadingMoradores(true);
    try {
      const response = await api.get('/contratados/moradores');
      setMoradores(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar moradores:', error);
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Erro ao carregar moradores.') });
    } finally {
      setLoadingMoradores(false);
    }
  }, [loadingMoradores, moradores.length]);

  useEffect(() => {
    fetchContratados();
  }, [fetchContratados]);

  const openModal = async (contratado = null) => {
    setFeedback(null);
    setContratadoParaEditar(contratado);
    setIsModalOpen(true);
    await fetchMoradores();
  };

  const handleModalClose = useCallback(
    async (result) => {
      setIsModalOpen(false);
      setContratadoParaEditar(null);
      if (result?.message) {
        setFeedback({ type: 'success', message: result.message });
      }
      await fetchContratados();
    },
    [fetchContratados]
  );

  const handleActionFeedback = useCallback(
    async (result) => {
      if (result?.message) {
        setFeedback({ type: result.type || 'success', message: result.message });
      }
      await fetchContratados();
    },
    [fetchContratados]
  );

  const handleStatusFilterChange = (nextStatus) => {
    setStatusFilter((current) => (current === nextStatus && nextStatus !== 'todos' ? 'todos' : nextStatus));
    setPage(1);
  };

  const firstItem = pagination.total_filtrado ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastItem = Math.min(page * PAGE_SIZE, pagination.total_filtrado);
  const canGoPrevious = page > 1;
  const canGoNext = page < pagination.total_pages;

  return (
    <PageLayout>
      <PageHeader
        title="Prestadores"
        description="Controle de entrada e saída de prestadores"
        actions={(
          <button
            onClick={() => openModal()}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-condo-dark px-5 py-2.5 font-medium text-white shadow-md transition hover:bg-slate-700 sm:w-auto"
          >
            <UserPlus size={20} /> Registrar Entrada
          </button>
        )}
      />

      <FeedbackBanner
        type={feedback?.type}
        message={feedback?.message}
        onClose={() => setFeedback(null)}
      />

      <SummaryGrid>
        <SummaryCard
          title="Total de Registros"
          value={pagination.total}
          color="blue"
          icon={<Users size={24} />}
          active={statusFilter === 'todos'}
          onClick={() => handleStatusFilterChange('todos')}
        />
        <SummaryCard
          title="Prestadores Presentes"
          value={pagination.total_presentes}
          color="green"
          icon={<DoorOpen size={24} />}
          active={statusFilter === 'presentes'}
          onClick={() => handleStatusFilterChange('presentes')}
        />
        <SummaryCard
          title="Registros Hoje"
          value={pagination.total_hoje}
          color="purple"
          icon={<History size={24} />}
          active={statusFilter === 'hoje'}
          onClick={() => handleStatusFilterChange('hoje')}
        />
      </SummaryGrid>

      <SearchField
        placeholder="Buscar prestador por nome ou documento..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <ContratadosTable
        contratados={contratados}
        loading={loading}
        onDataChanged={handleActionFeedback}
        onEdit={(contratado) => openModal(contratado)}
      />

      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
        <span>
          Exibindo {firstItem}-{lastItem} de {pagination.total_filtrado} registro(s)
        </span>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={!canGoPrevious || loading}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Pagina anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="min-w-24 text-center font-semibold text-slate-700">
            Pagina {page} de {pagination.total_pages}
          </span>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(pagination.total_pages, current + 1))}
            disabled={!canGoNext || loading}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Proxima pagina"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <ContratadoModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        moradores={moradores}
        contratadoParaEditar={contratadoParaEditar}
      />
    </PageLayout>
  );
};

export default ContratadosPage;
